<template>
  <vue-scroll class="page-dashboard view box grow flex column">
    <el-row :type="'flex'">
      <el-col :span="24">
        <h5>{{ welcomeMessage }}</h5>
        <div class="float-right subtitle2">
          Past 1 month
          <el-link @click="loadDocumentation('dashboard-observation-funnel')" :underline="false">
            <i class="material-icons">help_outline</i>
          </el-link>
        </div>
      </el-col>
    </el-row>
    <el-row :type="'flex'">
      <el-col class="chevrons" :xs="24" :sm="6" :md="6" :lg="6" :xl="6">
        <div class="card-base">
          <ICountUp
              class="iCountUp ml-10"
              :delay="eventsConsumedCounter.delay"
              :endVal="eventsConsumedCounter.endVal"
              :options="eventsConsumedCounter.options"
          />
          <span class="subtitle1 display-block">Events Consumed</span>
        </div>
      </el-col>
      <el-col class="chevrons" :xs="24" :sm="6" :md="6" :lg="6" :xl="6">
        <div class="card-base">
          <ICountUp
              class="iCountUp ml-20"
              :delay="localTrailCounter.delay"
              :endVal="pastWeekLocalTrailCount"
              :options="localTrailCounter.options"
          />
          <span class="subtitle1 ml-20 display-block">Trails Investigated</span>
        </div>
      </el-col>
      <el-col class="chevrons" :xs="24" :sm="6" :md="6" :lg="6" :xl="6">
        <div class="card-base">
          <ICountUp
              class="iCountUp ml-20"
              :delay="pastWeekIocCounter.delay"
              :endVal="pastWeekIocCount"
              :options="pastWeekIocCounter.options"
          />
          <span class="subtitle1 ml-20 display-block">Detections observed</span>
        </div>
      </el-col>
      <el-col
          class="chevrons cursor-pointer"
          :class="{ 'at-risk': threatProgressionExist }"
          :xs="24"
          :sm="6"
          :md="6"
          :lg="6"
          :xl="6"
          @click.native="gotoProgressionsView"
      >
        <div class="card-base no-padding full-width">
          <div class="full-width progression-count" v-loading="summaryApiStatus === 'inprogress'">
            <ICountUp
                class="iCountUp ml-20 light-bold"
                :delay="activeTrailCounter.delay"
                :endVal="pastWeekThreatProgresssionCount"
                :options="activeTrailCounter.options"
            />
            <span class="subtitle1 ml-20 display-block">Active Threat Progressions</span>
          </div>
        </div>
      </el-col>
    </el-row>
    <el-row :type="'flex'">
      <el-col :xs="24" :sm="6" :md="6" :lg="6" :xl="6">
        <asset-monitoring></asset-monitoring>
      </el-col>
      <el-col :xs="24" :sm="12" :md="12" :lg="12" :xl="12">
        <div class="card-base full-height">
          <div class="widget-header ph-20 pt-20 full-height">
            <span class="subtitle2" style="margin: 0px">
              <h5 class="display-inline-block activity-dial" style="margin: 0px">Today's Activity Dial</h5>
              {{ `${$moment().format('MMMM Do YYYY')}` }}
            </span>
            <el-link @click="loadDocumentation('dashboard-activity-dial')" :underline="false">
              <i class="material-icons">help_outline</i>
            </el-link>
            <div class="flex full-height dial-container">
              <div class="widget-icon-box box grow mr-20 full-height">
                <el-tabs v-model="activeActivityName" @tab-click="changeActivitySelection">
                  <el-tab-pane label="EXECUTION" name="executes">
                    <activity-radial-chart
                        v-if="activeActivityName === 'executes'"
                        :event-type="`executes`"
                    ></activity-radial-chart>
                  </el-tab-pane>
                  <el-tab-pane label="NETWORK" name="network">
                    <activity-radial-chart
                        v-if="activeActivityName === 'network'"
                        :event-type="`network`"
                    ></activity-radial-chart>
                  </el-tab-pane>
                  <el-tab-pane label="FILES" name="files">
                    <activity-radial-chart
                        v-if="activeActivityName === 'files'"
                        :event-type="`files`"
                    ></activity-radial-chart>
                  </el-tab-pane>
                  <el-tab-pane label="USERS" name="users">
                    <activity-radial-chart
                        v-if="activeActivityName === 'users'"
                        :event-type="`users`"
                    ></activity-radial-chart>
                  </el-tab-pane>
                </el-tabs>
              </div>
            </div>
          </div>
        </div>
      </el-col>
      <el-col :xs="24" :sm="6" :md="6" :lg="6" :xl="6" class="right-progression-widget">
        <threat-progression-list></threat-progression-list>
        <mitre-tactic-analysis :at-risk="threatProgressionExist"></mitre-tactic-analysis>
      </el-col>
    </el-row>
    <el-row :type="'flex'" class="box grow">
      <el-col :xs="24" :sm="6" :md="6" :lg="6" :xl="6">
        <dashboard-insights-widget
            :title="`Executes`"
            :type="`executes`"
            :rate-units="`exec/sec`"
            :key="getProgressKey('executes')"
        >
          <div slot="additional-info">
            <span v-if="environmentStatusCheck">
              <insights-info-card
                  v-if="agentEnvironment === 'linux'"
                  class="cursor-pointer"
                  title="New Privileged Programs"
                  :kpi-type="'executes'"
                  :insights-type="'privileged_linux'"
                  @click.native="gotoInsightsView('linux-privileged-executions-insights')"
              >
              </insights-info-card>
              <insights-info-card
                  v-else-if="agentEnvironment === 'windows'"
                  class="cursor-pointer"
                  title="New Privileged Programs"
                  :kpi-type="'executes'"
                  :insights-type="'privileged'"
                  @click.native="gotoInsightsView('windows-privileged-executions-insights')"
              >
              </insights-info-card>
              <insights-info-card
                  v-else
                  title="New Privileged Programs"
                  :kpi-type="['executes', 'executes']"
                  :insights-type="['privileged', 'privileged_linux']"
                  @insights-click="insightsClickAction"
              >
              </insights-info-card>
              <insights-info-card
                  v-if="['hybrid', 'linux'].indexOf(agentEnvironment) >= 0"
                  class="cursor-pointer"
                  title="New Process Launching Shells"
                  kpi-type="executes"
                  insights-type="process_launching_shells"
                  @click.native="gotoInsightsView('linux-process-launching-shells-insights')"
              >
              </insights-info-card>
            </span>
          </div>
        </dashboard-insights-widget>
      </el-col>
      <el-col :xs="24" :sm="6" :md="6" :lg="6" :xl="6">
        <dashboard-insights-widget
            :title="`Network`"
            :type="`network`"
            :rate-units="`ops/sec`"
            :key="getProgressKey('network')"
        >
          <div slot="additional-info">
            <insights-info-card
                :title="`Unique IP Inbound`"
                :kpi-type="`network`"
                :insights-type="`accept_externalips`"
            ></insights-info-card>
            <insights-info-card
                :title="`Unique IP Outbound`"
                :kpi-type="`network`"
                :insights-type="`connect_externalips`"
            ></insights-info-card>

            <span v-if="environmentStatusCheck">
              <insights-info-card
                  v-if="agentEnvironment === 'linux'"
                  class="cursor-pointer"
                  :title="`New Outbound Programs`"
                  :kpi-type="'network'"
                  :insights-type="'external_programs_linux'"
                  @click.native="gotoInsightsView('linux-external-connections-insights')"
              >
              </insights-info-card>
              <insights-info-card
                  v-else-if="agentEnvironment === 'windows'"
                  class="cursor-pointer"
                  :title="`New Outbound Programs`"
                  :kpi-type="'network'"
                  :insights-type="'external_programs'"
                  @click.native="gotoInsightsView('windows-external-connections-insights')"
              >
              </insights-info-card>
              <insights-info-card
                  v-else
                  :title="`New Outbound Programs`"
                  :kpi-type="['network', 'network']"
                  :insights-type="['external_programs', 'external_programs_linux']"
                  @insights-click="insightsClickAction"
              >
              </insights-info-card>
            </span>

            <insights-info-card
                class="cursor-pointer"
                :title="`New DNS Request`"
                :kpi-type="`network`"
                :insights-type="`dns`"
                @click.native="gotoInsightsView('windows-dns-requests-insights')"
            >
            </insights-info-card>
          </div>
        </dashboard-insights-widget>
      </el-col>
      <el-col :xs="24" :sm="6" :md="6" :lg="6" :xl="6">
        <dashboard-insights-widget
            :title="`Files`"
            :type="`files`"
            :rate-units="`ops/sec`"
            :key="getProgressKey('files')"
        >
          <div slot="additional-info">
            <insights-info-card
                class="cursor-pointer"
                :title="`Programs Creating Executables`"
                :kpi-type="`files`"
                :insights-type="`execs_creating_procs`"
                @insights-click="gotoThreatHunting"
            ></insights-info-card>
            <insights-info-card
                class="cursor-pointer"
                :title="`DLLs Created`"
                :kpi-type="`files`"
                :insights-type="`dlls_creating_procs`"
                @insights-click="gotoThreatHunting"
            ></insights-info-card>

            <span v-if="environmentStatusCheck">
              <insights-info-card
                  v-if="['hybrid', 'linux'].indexOf(agentEnvironment) >= 0"
                  class="cursor-pointer"
                  title="New Sensitive Files Access"
                  kpi-type="files"
                  insights-type="linux_sensitive_file_access"
                  @click.native="gotoInsightsView('linux-sensitive-file-access-insights')"
              >
              </insights-info-card>
            </span>
          </div>
        </dashboard-insights-widget>
      </el-col>
      <el-col :xs="24" :sm="6" :md="6" :lg="6" :xl="6">
        <dashboard-insights-widget
            :title="`Users`"
            :type="`users`"
            :rate-units="`logins/sec`"
            :key="getProgressKey('users')"
        >
          <div slot="additional-info">
            <anomalies-info-card
                class="cursor-pointer"
                title="Anomalies"
                :anomaly-type="'LOGIN_ANOMALY'"
                @click.native="gotoAnomaliesView"
            ></anomalies-info-card>
            <span v-if="environmentStatusCheck">
              <insights-info-card
                  v-if="agentEnvironment === 'linux'"
                  class="cursor-pointer"
                  title="Failed Login Attempts"
                  :kpi-type="'users'"
                  :insights-type="'login_failures_linux'"
                  @click.native="gotoInsightsView('linux-login-failures-insights')"
              >
              </insights-info-card>
              <insights-info-card
                  v-else-if="agentEnvironment === 'windows'"
                  class="cursor-pointer"
                  title="Failed Login Attempts"
                  :kpi-type="'users'"
                  :insights-type="'login_failures'"
                  @click.native="gotoInsightsView('windows-logon-failures-insights')"
              >
              </insights-info-card>
              <insights-info-card
                  v-else
                  title="Failed Login Attempts"
                  :kpi-type="['users', 'users']"
                  :insights-type="['login_failures', 'login_failures_linux']"
                  @insights-click="insightsClickAction"
              >
              </insights-info-card>
            </span>
          </div>
        </dashboard-insights-widget>
      </el-col>
    </el-row>
  </vue-scroll>
</template>

<script>
import ICountUp from 'vue-countup-v2'
import {mapGetters} from 'vuex'
import {registerInverval, destroyInverval} from '@/utilities/global.interval'
import {DASHBOARD_POLLING_INTERVAL} from '@/constants/polling-interval.constants'
import {SYSTEM_TRAIL_SUMMARY_REQ, SYSTEM_TRAIL_SUMMARY_REQ_RESET} from '@/store/modules/trails/trailsinfo.type'
import {
  IOC_DETECTIONS_LIST_RESET_STATUS,
  PRIVILEGED_ACTIONS_LIST_RESET_STATUS
} from '@/store/modules/detections/detections.type'
import {EVENTS_CONSUMED_REQ} from '@/store/modules/agents/agents.type'
import {LOCAL_TRAIL_COUNT_REQ} from '@/store/modules/dashboard/trail-statistics.type'
import PageHeaders from '@/components/layout/PageHeaders.vue'
import LineChartJs from '@/components/chart/LineChartJs.vue'
import UserButton from '@/components/button/UserButton.vue'
import DateRangeDropdown from '@/components/dropdown/DateRangeDropdown.vue'
import MitreTacticAnalysis from '@/components/dashboard/MitreTacticAnalysis.vue'
import AssetMonitoring from '@/components/dashboard/AssetMonitoring.vue'
import DashboardInsightsWidget from '@/components/dashboard/DashboardInsightsWidget.vue'
import InsightsInfoCard from '@/components/dashboard/InsightsInfoCard.vue'
import AnomaliesInfoCard from '@/components/dashboard/AnomaliesInfoCard.vue'
import ActivityRadialChart from '@/components/dashboard/ActivityRadialChart.vue'
import ThreatProgressionList from '@/components/dashboard/ThreatProgressionList.vue'
import loadDocumentationMixin from '@/mixins/loadDocumentationMixin'

export default {
  name: 'HomeDashboard',
  mixins: [loadDocumentationMixin],
  data() {
    return {
      activeActivityName: 'executes',
      summaryIntervalId: {},
      summaryApiStatus: '',
      eventsConsumedCounter: {
        delay: 1000,
        endVal: 0,
        options: {
          useEasing: true,
          useGrouping: true,
          separator: ','
        }
      },
      localTrailCounter: {
        delay: 1000,
        endVal: 0,
        options: {
          useEasing: true,
          useGrouping: true,
          separator: ','
        }
      },
      pastWeekIocCounter: {
        delay: 1000,
        endVal: 0,
        options: {
          useEasing: true,
          useGrouping: true,
          separator: ','
        }
      },
      activeTrailCounter: {
        delay: 1000,
        endVal: 0,
        options: {
          useEasing: true,
          useGrouping: true,
          separator: ','
        }
      }
    }
  },
  created() {
    if (!this.insightsEnabled) {
      this.$router.push({name: 'dashboard-trails'})
      return false
    }
    // on load of this view, collapse the sidebar nav (this view is two levels deep from App.vue)
    if (!_.isUndefined(this.$parent.collapseNav)) {
      this.$set(this.$parent, 'collapseNav', true)
    }
    // initial load, call backend else use cached data
    this.summaryApiStatus = 'inprogress'
    if (this.activeTrailSummaryStatus === 'saved') {
      this.eventsConsumedCounter.endVal = this.trailSummary.numEventsProcessed
      this.pollTrailSummaryInfo()
    } else {
      this.getTrailSummaryInfo()
    }
  },
  methods: {
    changeActivitySelection(tab, event) {
      console.log(tab, event)
    },
    pollTrailSummaryInfo() {
      this.summaryIntervalId = setTimeout(() => {
        this.getTrailSummaryInfo()
      }, DASHBOARD_POLLING_INTERVAL)
      registerInverval(this.summaryIntervalId)
    },
    getTrailSummaryInfo() {
      if (this.summaryIntervalId) {
        destroyInverval(this.summaryIntervalId)
      }
      Promise.all([
        this.$store.dispatch(SYSTEM_TRAIL_SUMMARY_REQ, 'active'),
        this.$store.dispatch(EVENTS_CONSUMED_REQ, 'PAST_1_MONTH'),
        this.$store.dispatch(LOCAL_TRAIL_COUNT_REQ, 'PAST_1_MONTH')
      ]).then(
          summary => {
            this.summaryApiStatus = 'saved'
            this.eventsConsumedCounter.endVal = summary[1] ? summary[1] : 0
            this.pollTrailSummaryInfo()
          },
          errMsg => {
            this.summaryApiStatus = 'error'
            this.pollTrailSummaryInfo()
          }
      )
    },
    getProgressKey(type) {
      return `${type}_${this.threatProgressionExist}`
    },
    gotoProgressionsView() {
      this.$router.push({
        name: 'active-cyber-attacks',
        query: {date_range: 'PAST_1_MONTH'}
      })
    },
    gotoInsightsView(name) {
      this.$router.push({
        name: name,
        query: {date_range: 'PAST_24_HOURS'}
      })
    },
    gotoAnomaliesView(name) {
      this.$router.push({
        name: 'anomalies-detections',
        query: {date_range: 'PAST_24_HOURS'}
      })
    },
    gotoThreatHunting(insightsType, sourceComms) {
      console.log('test')
      let threatHuntParam = `date_range:PAST_24_HOURS+edgeName:in:file:exec+sourceComm:in`
      _.each(sourceComms[0], value => {
        threatHuntParam += ':' + value.sourceComm
      })
      switch (insightsType) {
        case 'dlls_creating_procs':
          threatHuntParam += '+targetPathName:matches:.*dll'
          break
        case 'execs_creating_procs':
          threatHuntParam += '+targetPathName:matches:.*exe'
          break
      }
      this.$router.push({
        name: 'threat-hunting',
        query: {search: threatHuntParam}
      })
    },
    insightsClickAction(kpi) {
      let name = ''
      switch (kpi) {
        case 'login_failures':
          name = 'windows-logon-failures-insights'
          break
        case 'login_failures_linux':
          name = 'linux-login-failures-insights'
          break
        case 'privileged':
          name = 'windows-privileged-executions-insights'
          break
        case 'privileged_linux':
          name = 'linux-privileged-executions-insights'
          break
        case 'external_programs':
          name = 'windows-external-connections-insights'
          break
        case 'external_programs_linux':
          name = 'linux-external-connections-insights'
          break
        default:
          break
      }
      this.$router.push({
        name: name,
        query: {date_range: 'PAST_24_HOURS'}
      })
    }
  },
  computed: {
    welcomeMessage() {
      return `Welcome back, ${this.userProfile.firstname} ${this.userProfile.lastname}`
    },
    environmentStatusCheck() {
      return this.agentListReqStatus === 'saved' || this.agentEnvironment !== null
    },
    pastWeekIocCount() {
      return this.iocTacticSummary.iocCount || 0
    },
    pastWeekLocalTrailCount() {
      return this.localTrailCount || 0
    },
    pastWeekThreatProgresssionCount() {
      let activeTrailCount = this.activeTrailSummary.numTrails || 0
      return activeTrailCount
    },
    threatProgressionExist() {
      return this.activeTrailSummary.numTrails > 0
    },
    ...mapGetters([
      'userProfile',
      'activeTrailSummary',
      'activeTrailSummaryStatus',
      'eventsConsumed',
      'iocTacticSummary',
      'localTrailCount',
      'insightsEnabled',
      'agentEnvironment',
      'agentListReqStatus'
    ]),
    summaryInfoLineChartOptions() {
      return {
        tooltips: {enabled: false},
        elements: {
          point: {pointStyle: 'line'},
          line: {
            fill: true,
            backgroundColor: 'rgb(242, 248, 255)',
            borderColor: 'rgb(0, 112, 249)'
          }
        }
      }
    }
  },
  beforeDestroy() {
    // reset summary req call
    this.$store.dispatch(SYSTEM_TRAIL_SUMMARY_REQ_RESET, 'active')
    this.$store.dispatch(IOC_DETECTIONS_LIST_RESET_STATUS)
    this.$store.dispatch(PRIVILEGED_ACTIONS_LIST_RESET_STATUS)
    // kill interval calls
    this.pollTrailSummaryInfo = function () {
    }
    destroyInverval(this.summaryIntervalId)
  },
  components: {
    LineChartJs,
    UserButton,
    PageHeaders,
    ICountUp,
    DateRangeDropdown,
    MitreTacticAnalysis,
    AssetMonitoring,
    DashboardInsightsWidget,
    InsightsInfoCard,
    AnomaliesInfoCard,
    ActivityRadialChart,
    ThreatProgressionList
  },
  watch: {}
}
</script>

<style>
.c-tooltip {
  max-width: 200px;
}
</style>

<style scoped>
@import 'assets/styles/_variables.css';
@import 'assets/styles/colors.css';
@import 'assets/styles/riskscore_colors.css';

.layout-container .container .view {
  padding: 0px;
  overflow: hidden;
}

.page-headers {
  min-height: 45px;
  margin: 20px 10px 0px !important;
  padding: 0px 15px;
}

.material-icons {
  font-size: 20px;
  color: var(--aluminum-400);
  line-height: 27px;
  vertical-align: text-top;
}

.privilege-content {
  padding-left: 10px;
}

.icon-privilege-activities {
  height: 24px;
  width: 24px;
  margin: auto;
  background: var(--denim-700);
  mask: url('/static/images/privilege_symbol.svg');
  mask-size: cover;
}

.el-row {
  background: white;
  padding: 0px 25px;

  &:first-of-type {
    padding: 10px 25px;
  }
}

.el-card {
  height: 100%;
  border-top: none;
  border-right: none;
  border-bottom: 1px solid var(--aluminum-400);
  border-left: 1px solid var(--aluminum-400);
  border-radius: 0px;

  >>> .el-card__body {
    padding: 16px !important;
  }
}

.avatar-wrapper {
  align-items: stretch;
}

.avatar-circle {
  color: white;
  letter-spacing: -1px;
  font-size: 13px;
  height: 32px;
  width: 32px;
  padding: 0px;
  margin: -3px;
  position: relative;

  &.active {
    border: 2px solid white;
  }

  &.active::after {
    content: '';
    border: 2px solid #197de6;
    height: 32px;
    width: 32px;
    border-radius: 50%;
    position: absolute;
    left: -4px;
    top: -4px;
  }
}

.el-card.active-card {
  height: calc(100% - 3px);
  border-top: 4px solid var(--denim-600);
  border-bottom: none;
}

.card-header {
  > div.with-border:first-of-type {
    border-right: 1px solid var(--border-color);
  }

  > div {
    padding: 0px 10px;
  }

  span {
    display: block;
  }

  .system-summary_events {
    span {
      display: inline-block;
      line-height: 16px;

      &:last-of-type {
        position: relative;
        top: 10px;
      }
    }
  }
}

.card-content {
  margin-top: 5%;

  > div:first-of-type {
    padding-bottom: 10px;
  }

  > div:nth-child(n + 2) {
    padding-bottom: 5px;
  }

  > div:last-of-type {
  }
}

.system-glance {
  .card-content {
    margin-top: 15%;

    > div:first-of-type {
      padding-top: 10px;
      border-top: 1px solid var(--border-color);
    }
  }
}

.system-summary {
  background-color: var(--background-color);
  border-right: 1px solid var(--aluminum-400);

  .summary-chart {
    width: 85%;
    max-width: 85%;
  }

  .col-3 {
    z-index: 99;
    background: white;
  }
}

.summary-pp {
  .greeting {
    font-weight: bold;
    margin: 0px;
  }

  .summary-info {
    font-size: 0.8em;
    margin: 0.5em;
  }
}

.offline-color {
  border: 1px solid var(--offline-color);
}

.online-color {
  border: 1px solid var(--online-color);
}

.page-dashboard {
  .el-row:nth-child(3) {
    min-height: 600px;
    height: calc(100% - 550px);
    padding-top: 5px;
    padding-bottom: 5px;

    .el-col {
      &:first-of-type {
        margin-right: 5px;
      }

      &:last-of-type {
        margin-left: 5px;
      }

      &:not(:nth-child(2)):not(.right-progression-widget) {
        border: 1px solid var(--border-color);
      }
    }

    >>> .el-tabs {
      height: 100%;
    }

    >>> .el-tabs__content {
      height: 100%;
    }

    >>> .el-tab-pane {
      height: calc(100% - 75px);
    }
  }

  .el-row:nth-child(n + 4) {
    min-height: 350px;
    padding-top: 5px;
    padding-bottom: 5px;

    .el-col {
      border: 1px solid var(--border-color);

      &:not(:last-of-type) {
        margin-right: 5px;
      }
    }
  }

  .right-progression-widget {
    .card-base {
      border: 1px solid var(--border-color);
    }

    > div:first-of-type {
      height: calc(100% - 315px);
      margin-bottom: 10px;

      >>> .widget-header {
        height: 100%;

        div.flex.justify-center {
          height: calc(100% - 35px);
        }

        .card-base.widget-info {
          height: 100%;
        }

        .c-table {
          height: calc(100% - 52px);
        }
      }
    }

    > div {
      height: 300px;
    }

    .el-divider--horizontal {
      margin: 0px;
    }
  }

  .el-divider--horizontal {
    display: block;
    height: 1px;
    width: calc(100% - 20px);
    margin-left: 10px;
    margin-top: 10px;
    margin-bottom: 0px;
  }

  .no-padding {
    padding: 0px !important;
  }

  .card-base {
    border-radius: 0px;

    h5 {
      line-height: 25px;
    }

    span {
      line-height: 15px;
    }

    h5,
    span {
      margin-left: 10px;

      &.ml-20 {
        margin-left: 20px;
      }

      &.ml-0 {
        margin-left: 0px;
      }
    }

    .iCountUp {
      font-size: 24px;
      font-weight: normal;
      line-height: 25px;
      color: inherit;
      margin: unset;
      margin-left: 10px;
      letter-spacing: -0.35px;

      &.light-bold {
        font-weight: 600;
      }
    }
  }

  .chevrons {
    background-color: white;

    h5 {
      height: 25px;
    }

    .card-base {
      width: 95%;
      border-radius: 0px;
      display: inline-block;
      background-color: var(--aluminum-100);
      padding: 5px 0px;

      &.full-width {
        width: 100%;
      }

      > .progression-count {
        padding: 5px 0px;
      }
    }

    &:not(:first-of-type):before {
      content: '';
      border-top: 25px solid var(--aluminum-100);
      border-bottom: 25px solid var(--aluminum-100);
      border-left: 12.5px solid white;
      height: 0;
      position: absolute;
    }

    &:not(:last-of-type):after {
      content: '';
      border-top: 25px solid transparent;
      border-bottom: 25px solid transparent;
      border-right: 12.5px solid transparent;
      border-left: 12.5px solid var(--aluminum-100);
      height: 0;
      position: absolute;
    }

    &:last-of-type {
      &.at-risk {
        .card-base {
          background-color: var(--amaranth-400);
        }

        &:before {
          border-top: 25px solid var(--amaranth-400);
          border-bottom: 25px solid var(--amaranth-400);
        }
      }

      .card-base {
        color: white;
        background-color: var(--denim-500);
      }

      &:before {
        border-top: 25px solid var(--denim-500);
        border-bottom: 25px solid var(--denim-500);
      }
    }
  }

  >>> .el-tabs__item {
    font-size: 12px;
    font-weight: normal;
    color: inherit;
    line-height: 32px;
    height: 32px;
    text-transform: lowercase;

    &:first-letter {
      text-transform: uppercase;
    }

    letter-spacing: 1px;
  }

  .dial-container {
    justify-content: center;
    padding-left: 21px;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/dashboard/HomeDashboard.vue